import './App.css';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Home from './Home/Home';
import Contact from './Contact/Contact';
import About from './About/About';
import PrivacyPolicy from './PrivacyPolicy/PrivacyPolicy';
import TermsAndConditions from './TermsAndConditions/TermsAndConditions';
import CookiesPolicy from './CookiesPolicy/CookiesPolicy';
import Sitemap from './Sitemap/Sitemap';
import Portfolio from './Portfolio/Portfolio';
import ProjectDetail from './Portfolio/ProjectDetail';
import Process from './Process/Process';
import ServiceLanding from './ServiceLanding/ServiceLanding';
import AdminLogin from './Admin/AdminLogin';
import Dashboard from './Admin/Dashboard';
import AdminServices from './Admin/AdminServices';
import AdminSocials from './Admin/AdminSocials';
import AdminInquiries from './Admin/AdminInquiries';
import AdminPortfolio from './Admin/AdminPortfolio';
import AdminAbout from './Admin/AdminAbout';
import AdminContactPage from './Admin/AdminContact';
import AdminPrivacy from './Admin/AdminPrivacy';
import AdminTerms from './Admin/AdminTerms';
import AdminCookies from './Admin/AdminCookies';
import AdminInvoice from './Admin/AdminInvoice';
import AdminBlog from './Admin/AdminBlog';
import AdminBrochure from './Admin/AdminBrochure';
import AdminRefundPolicy from './Admin/AdminRefundPolicy';
import AdminClients from './Admin/AdminClients';
import AdminPayments from './Admin/AdminPayments';
import AdminTasks from './Admin/AdminTasks';
import ClientOnboarding from './ClientOnboarding/ClientOnboarding';
import ClientLogin from './ClientLogin/ClientLogin';
import ClientDashboard from './ClientDashboard/ClientDashboard';
import RefundPolicy from './RefundPolicy/RefundPolicy';
import Blog from './Blog/Blog';
import Brochure from './Brochure/Brochure';
import Pricing from './Pricing/Pricing';
import BlogDetail from './Blog/BlogDetail';
import ProtectedRoute from './Admin/ProtectedRoute';
import NotFound from './NotFound/NotFound';
import Cursor from './components/Cursor';
import WhatsAppButton from './components/WhatsAppButton';
import { Toaster } from 'react-hot-toast';
import TrafficTracker from './components/TrafficTracker';

/* Page transition variants */
const pageVariants = {
  initial: { opacity: 0, y: 18 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.32, ease: [0.25, 0.46, 0.45, 0.94] },
  },
  exit: {
    opacity: 0,
    y: -12,
    transition: { duration: 0.22, ease: [0.55, 0, 1, 0.45] },
  },
};

/* All routes live here so useLocation() works inside <Router> */
function AnimatedRoutes() {
  const location = useLocation();
  const isOnboarding = location.pathname === '/client-onboarding';

  /* Onboarding renders standalone — no navbar, cursor, whatsapp, tracker */
  if (isOnboarding) return <ClientOnboarding />;
  if (location.pathname === '/client-login') return <ClientLogin />;
  if (location.pathname.startsWith('/client-dashboard')) return <ClientDashboard />;

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        style={{ minHeight: '100vh' }}
      >
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-conditions" element={<TermsAndConditions />} />
          <Route path="/cookie-policy" element={<CookiesPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/sitemap" element={<Sitemap />} />
          <Route path="/portfolio" element={<Portfolio />} />
          <Route path="/portfolio/:id" element={<ProjectDetail />} />
          <Route path="/process" element={<Process />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />
          <Route path="/brochure" element={<Brochure />} />

          {/* SEO Service Landing Pages */}
          <Route path="/web-development-vadodara" element={<ServiceLanding />} />
          <Route path="/flutter-app-development" element={<ServiceLanding />} />
          <Route path="/mobile-app-development" element={<ServiceLanding />} />
          <Route path="/ui-ux-design-services" element={<ServiceLanding />} />
          <Route path="/:serviceId" element={<ServiceLanding />} />

          <Route path="/admin/login" element={<AdminLogin />} />

          {/* Protected Admin Routes */}
          <Route path="/admin/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/admin/services" element={<ProtectedRoute><AdminServices /></ProtectedRoute>} />
          <Route path="/admin/socials" element={<ProtectedRoute><AdminSocials /></ProtectedRoute>} />
          <Route path="/admin/inquiries" element={<ProtectedRoute><AdminInquiries /></ProtectedRoute>} />
          <Route path="/admin/portfolio" element={<ProtectedRoute><AdminPortfolio /></ProtectedRoute>} />
          <Route path="/admin/about" element={<ProtectedRoute><AdminAbout /></ProtectedRoute>} />
          <Route path="/admin/contact-page" element={<ProtectedRoute><AdminContactPage /></ProtectedRoute>} />
          <Route path="/admin/privacy" element={<ProtectedRoute><AdminPrivacy /></ProtectedRoute>} />
          <Route path="/admin/terms" element={<ProtectedRoute><AdminTerms /></ProtectedRoute>} />
          <Route path="/admin/cookies" element={<ProtectedRoute><AdminCookies /></ProtectedRoute>} />
          <Route path="/admin/invoice" element={<ProtectedRoute><AdminInvoice /></ProtectedRoute>} />
          <Route path="/admin/blog" element={<ProtectedRoute><AdminBlog /></ProtectedRoute>} />
          <Route path="/admin/brochure" element={<ProtectedRoute><AdminBrochure /></ProtectedRoute>} />
          <Route path="/admin/refund-policy" element={<ProtectedRoute><AdminRefundPolicy /></ProtectedRoute>} />
          <Route path="/admin/clients" element={<ProtectedRoute><AdminClients /></ProtectedRoute>} />
          <Route path="/admin/payments" element={<ProtectedRoute><AdminPayments /></ProtectedRoute>} />
          <Route path="/admin/tasks" element={<ProtectedRoute><AdminTasks /></ProtectedRoute>} />

          {/* 404 */}
          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  return (
    <Router>
      <div className="App">
        <Toaster position="top-right" reverseOrder={false} />
        <TrafficTracker />
        <Cursor />
        <WhatsAppButton />
        <AnimatedRoutes />
      </div>
    </Router>
  );
}

export default App;
